<template>
  <div class="mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <span style="font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.85);">
        <i class="bi bi-building text-primary mr-1"></i> Reservations
      </span>
      <a href="/reservations" class="text-primary" style="font-size:12px;font-weight:600;">
        Open bookings <i class="bi bi-chevron-right" style="font-size:10px;"></i>
      </a>
    </div>

    <div v-if="loading && !d" class="row" style="row-gap:20px;">
      <div v-for="n in 2" :key="n" class="col-12" :class="n === 1 ? 'col-xl-8' : 'col-xl-4'">
        <div class="bg-white border" :style="card" style="height:300px;"></div>
      </div>
    </div>

    <p v-else-if="error" class="text-danger" style="font-size:13px;">{{ error }}</p>

    <div v-else-if="d && !d.rooms" class="bg-white border" :style="card">
      <p class="text-secondary mb-0" style="font-size:13px;">No rooms yet. Mark products as <strong>Reservable</strong> in Inventory to see occupancy here.</p>
    </div>

    <div v-else-if="d" class="row" style="row-gap:20px;">

      <!-- KPIs + occupancy/income chart -->
      <div class="col-12 col-xl-8">
        <div class="bg-white border h-100" :style="card">
          <div class="row mb-3" style="row-gap:18px;">
            <div v-for="s in stats" :key="s.label" class="col-6 col-md-4">
              <div class="text-secondary mb-1" style="font-size:12px;">
                {{ s.label }}
                <i v-if="s.help" class="bi bi-question-circle" style="font-size:11px;" :title="s.help"></i>
              </div>
              <span :class="s.danger ? 'text-danger' : 'text-dark'" style="font-size:18px;font-weight:700;letter-spacing:-0.2px;">{{ s.value }}</span>
            </div>
          </div>
          <div class="d-flex flex-wrap mb-2" style="gap:14px;">
            <span class="d-inline-flex align-items-center text-secondary" style="font-size:12px;gap:6px;">
              <span style="width:10px;height:10px;border-radius:3px;background:#fd4719;"></span> Room income
            </span>
            <span class="d-inline-flex align-items-center text-secondary" style="font-size:12px;gap:6px;">
              <span style="width:14px;height:2px;background:#343a40;"></span> Occupancy %
            </span>
          </div>
          <div style="position:relative;height:220px;">
            <canvas ref="combo"></canvas>
          </div>
        </div>
      </div>

      <!-- Today -->
      <div class="col-12 col-xl-4">
        <div class="bg-white border h-100 d-flex flex-column" :style="card">
          <div class="mb-3" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Today at the front desk</div>
          <div v-for="t in todayRows" :key="t.label" class="d-flex justify-content-between align-items-center py-2 border-bottom" style="font-size:14px;">
            <span class="text-secondary">{{ t.label }}</span>
            <strong class="text-dark" style="font-size:18px;">{{ t.value }}</strong>
          </div>
          <div class="d-flex justify-content-between align-items-center py-2" style="font-size:14px;">
            <span class="text-secondary">Rooms</span>
            <strong class="text-dark" style="font-size:18px;">{{ d.rooms }}</strong>
          </div>
          <a href="/reservations-today" class="btn btn-primary btn-block mt-auto">Open Today</a>
        </div>
      </div>

      <!-- By room -->
      <div class="col-12 col-lg-7">
        <div class="bg-white border h-100" :style="card">
          <div class="mb-3" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Income by room <i class="bi bi-question-circle text-secondary" style="font-size:11px;" title="Room nights sold in the selected dates, at each night's rate (incl. VAT). % = how many of the nights the room was taken."></i></div>
          <div v-for="r in d.by_room" :key="r.name" class="mb-3">
            <div class="d-flex justify-content-between" style="font-size:13px;">
              <span class="text-dark">{{ r.name }} <span class="text-secondary">· {{ r.nights }} night{{ r.nights === 1 ? '' : 's' }} · {{ r.occupancy }}%</span></span>
              <strong class="text-dark">{{ money(r.revenue) }}</strong>
            </div>
            <div class="progress mt-1" style="height:6px;">
              <div class="progress-bar bg-primary" :style="{ width: pct(r.revenue, roomMax) + '%' }"></div>
            </div>
          </div>
          <p v-if="!d.by_room.length" class="text-secondary mb-0" style="font-size:12px;">No room nights in this period.</p>
        </div>
      </div>

      <!-- Sources -->
      <div class="col-12 col-lg-5">
        <div class="bg-white border h-100" :style="card">
          <div class="mb-3" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Where bookings come from</div>
          <div v-for="s in d.sources" :key="s.source" class="mb-3">
            <div class="d-flex justify-content-between" style="font-size:13px;">
              <span :class="s.source === 'Not recorded' ? 'text-secondary' : 'text-dark'">{{ s.source }}</span>
              <strong class="text-dark">{{ s.count }}</strong>
            </div>
            <div class="progress mt-1" style="height:6px;">
              <div class="progress-bar" :class="s.source === 'Not recorded' ? 'bg-secondary' : 'bg-dark'" :style="{ width: pct(s.count, sourceMax) + '%' }"></div>
            </div>
          </div>
          <p v-if="!d.sources.length" class="text-secondary mb-0" style="font-size:12px;">No bookings made in this period.</p>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

/** Reservations block on /analytics/beta (companies with the reservations module). */
export default {
  name: 'ReservationBlock',

  props: {
    startDate: { type: String, required: true },
    endDate:   { type: String, required: true },
  },

  data() {
    return {
      d: null,
      loading: false,
      error: null,
      card: 'border-radius:14px;border-color:#e9ecef !important;padding:20px;',
    };
  },

  computed: {
    stats() {
      const k = this.d.kpis;
      return [
        { label: 'Occupancy', value: k.occupancy + '%', help: 'Room nights sold ÷ room nights available (rooms × days) in the selected dates.' },
        { label: 'Avg rate / night', value: this.money(k.adr), help: 'Room income ÷ room nights sold (ADR).' },
        { label: 'Income per room', value: this.money(k.revpar), help: 'Room income ÷ room nights available (RevPAR) - occupancy and rate in one number.' },
        { label: 'Room nights sold', value: k.room_nights.toLocaleString('en-US') },
        { label: 'Avg stay', value: k.avg_stay + ' night' + (k.avg_stay === 1 ? '' : 's') },
        { label: 'Cancelled', value: `${k.cancelled} (${k.cancel_rate}%)`, danger: k.cancelled > 0, help: `${k.bookings} booking(s) made in the period; ${k.no_shows} no-show(s).` },
      ];
    },

    todayRows() {
      const t = this.d.today;
      return [
        { label: 'Arriving', value: t.arrivals },
        { label: 'Leaving', value: t.departures },
        { label: 'In house', value: t.in_house },
      ];
    },

    roomMax() { return Math.max(1, ...this.d.by_room.map(r => r.revenue)); },
    sourceMax() { return Math.max(1, ...this.d.sources.map(s => s.count)); },
  },

  watch: {
    startDate() { this.load(); },
    endDate() { this.load(); },
  },

  mounted() { this.load(); },

  beforeDestroy() { if (this.chart) this.chart.destroy(); },

  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/sales-analytics/reservations', {
          params: { start_date: this.startDate, end_date: this.endDate },
        });
        this.d = data;
        this.$nextTick(this.render);
      } catch (e) {
        this.error = e.response?.data?.message || 'Could not load reservation numbers.';
      } finally {
        this.loading = false;
      }
    },

    render() {
      if (this.chart) this.chart.destroy();
      if (!this.$refs.combo) return;
      const c = this.d.chart;

      this.chart = new Chart(this.$refs.combo.getContext('2d'), {
        data: {
          labels: c.labels,
          datasets: [
            { type: 'bar', label: 'Room income', data: c.income, backgroundColor: '#fd4719', borderRadius: 4, maxBarThickness: 26, yAxisID: 'y', order: 2 },
            { type: 'line', label: 'Occupancy %', data: c.occupancy, borderColor: '#343a40', backgroundColor: '#343a40', borderWidth: 2, pointRadius: 2, tension: 0.35, yAxisID: 'y1', order: 1 },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 200 },
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#1e293b', padding: 10, cornerRadius: 6,
              callbacks: { label: ctx => ctx.dataset.yAxisID === 'y' ? ` Income: ${this.money(ctx.parsed.y)}` : ` Occupancy: ${ctx.parsed.y}%` },
            },
          },
          scales: {
            x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8', maxRotation: 0, autoSkip: true } },
            y: { beginAtZero: true, position: 'left', grid: { color: 'rgba(0,0,0,0.04)' }, border: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8', callback: v => this.short(v) } },
            y1: { beginAtZero: true, max: 100, position: 'right', grid: { display: false }, border: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8', callback: v => v + '%' } },
          },
        },
      });
    },

    pct(v, max) { return Math.max(2, Math.round(v / max * 100)); },
    money(v) { return 'KES ' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 }); },
    short(v) {
      if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
      if (v >= 1e3) return (v / 1e3).toFixed(0) + 'K';
      return Math.round(v);
    },
  },
};
</script>
